<template>
  <div class="members-manage">
    <PageTitle
      style="margin-bottom: 25px"
      :title="$t('member.membersManage.title')"
      :btn="$t('member.common.button.create.text')"
      btnFeature="admin.registerMember.page"
      :btn2="$t('member.common.button.export.text')"
      btn2Feature="admin.member.createSheetExportTask"
      @btnClick="handleCreateMember"
      @btn2Click="showExportOptions = true"
    />

    <FiltersContainer>
      <SearchTextInput
        class="flex-grow-1"
        :value.sync="search.searchText"
        :placeholder="$t('member.membersManage.searchPlaceholder')"
        @refresh="refresh(true)"
      />
      <TagSearch
        class="flex-grow-1"
        :model.sync="search.tags"
        :tags="tags"
        :preserve-null="true"
        @update:model="refresh(true, 'change')"
        @clear="refresh(true)"
      />
    </FiltersContainer>

    <CustomUserInfoFieldsSelect
      style="margin-bottom: 12px"
      @change="onCustomFieldFilterChange"
      @clear="onCustomFieldFilterClear"
    />

    <section class="flex justify-between items-end" style="gap: 12px">
      <!-- 只看自己顧客的帳號（服務人員）看不到全店會員數，不顯示註冊數上限（CNCY-10961） -->
      <template v-if="showMemberLimit">
        <MemberLimitProgressBar ref="memberLimitProgressBarRef" class="flex-1" />
        <a :href="bobLineAt" target="_blank" rel="noopener noreferrer">
          <BaseElButton>
            <div class="flex">
              <img src="@/assets/icon/chat.svg">
              <span style="margin-left: 6px">{{ $t('member.membersManage.quotaAdjust') }}</span>
            </div>
          </BaseElButton>
        </a>
      </template>
      <div v-else class="flex-1" />

      <TableFilter
        style="margin-left: 15px"
        :tableName="tableName"
        :options="displayTableFilterOptions"
        @update="updateTableFilter"
      />
    </section>

    <section v-loading="loading">
      <!-- <BaseTable empty-text="暫無數據" :data="membersList" @row-click="$router.push('/members/member-profile')"> -->
      <BaseTable v-if="!tableRefresh" :empty-text="$t('member.membersManage.emptyText')" :data="displayTableData">
        <EmptyBlock slot="empty" />
        <BaseElTableColumn
          prop="UserInfo.name"
          :label="$t('member.common.name.text')"
          fixed
          align="center"
          width="100"
        />
        <BaseElTableColumn
          v-if="showColumn('lineDisplayName')"
          :label="$t('member.membersManage.lineFriendName')"
          align="center"
          width="150"
        >
          <template slot-scope="scope">
            {{ scope.row.MemberAuthLineObject && scope.row.MemberAuthLineObject.displayName ? scope.row.MemberAuthLineObject.displayName : '-' }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn
          v-if="showColumn('phone')"
          prop="UserInfo.phone"
          :label="$t('member.common.phone.text')"
          width="150"
          align="center"
        />
        <BaseElTableColumn
          v-if="showColumn('birthday')"
          prop="UserInfo.birthday"
          :label="$t('member.common.birthday.text')"
          width="150"
          align="center"
        >
          <template v-slot="{row}">
            {{ row.UserInfo.birthday ? formatDate(row.UserInfo.birthday, 'YYYY-MM-DD') : '-' }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showMemberLevel && showColumn('level')" prop="level" :label="$t('member.common.level.text')" align="center">
          <template slot-scope="scope">
            {{ getDisplayLevel(scope.row) }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('tag')" prop="MTags" :label="$t('member.common.tag.text')" width="120" align="center">
          <template slot-scope="scope">
            <Tag
              v-for="(tag, index) in scope.row.MTags"
              v-show="index < 2"
              :key="tag.id"
              type="action"
              style="margin-right: 3px; margin-bottom:3px;"
              disable-transitions
            >
              {{ tag.name }}
            </Tag>
            <span v-if="scope.row.MTags.length === 0">-</span>
          </template>
        </BaseElTableColumn>

        <!-- 自訂義欄位資料 -->
        <template
          v-for="field in displayCustomFieldColumns"
        >
          <BaseElTableColumn
            v-if="showColumn(field.key)"
            :key="field.key"
            width="150"
            align="center"
            :prop="`customFields.${field.key}`"
            :label="field.name"
          />
        </template>

        <BaseElTableColumn
          v-if="showColumn('address')"
          prop="UserInfo.address"
          :label="$t('member.common.address.text')"
          align="center"
          show-overflow-tooltip
        />
        <BaseElTableColumn
          v-if="showColumn('loginType')"
          prop="UserInfo.auth"
          :label="$t('member.membersManage.loginType')"
          align="center"
        >
          <template slot-scope="scope">
            <div class="login-auth">
              <span v-if="!scope.row.User.AuthLine && !scope.row.User.AuthFB && !scope.row.User.AuthPhoneAccount">-</span>
              <img v-if="scope.row.User.AuthLine" src="@/assets/member/icon-auth-line.svg" alt="Line auth icon">
              <img v-if="scope.row.User.AuthFB" src="@/assets/member/icon-auth-facebook.svg" alt="Facebook auth icon">
              <img v-if="scope.row.User.AuthPhoneAccount" src="@/assets/member/icon-auth-phone.svg" alt="Phone Account auth icon">
            </div>
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showColumn('blackList')" prop="memberStatus" :label="$t('member.membersManage.userStatus')" align="center">
          <template slot-scope="scope">
            <Tag :type="memberStatusTagType(scope.row.memberStatus, scope.row.isBlock)">{{ memberStatusLabel(scope.row.memberStatus, scope.row.isBlock) }}</Tag>
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn fixed="right" :label="$t('member.common.action.text')" width="100" align="center">
          <template slot-scope="scope">
            <TableEditBtnGroup
              :editBtn="$t('member.common.view.text')"
              hideDelete
              @edit="goProfile(scope.row)"
            />
          </template>
        </BaseElTableColumn>
      </BaseTable>

      <Pagination
        v-if="!loading"
        :curPage.sync="tableOptions.page"
        :pageLimit="tableOptions.pageLimit"
        :total="membersCount"
        @pageChange="changePage"
      />
    </section>

    <!-- 編輯會員 Dialog -->
    <el-dialog
      :title="$t('member.membersManage.editMember')"
      :visible.sync="showDialog"
      :close-on-click-modal="false"
      width="560px"
      @close="resetForm()"
    >
      <BaseElForm
        v-if="!showInput(['record'])"
        ref="form"
        :model="modifyFormData"
        label-position="left"
        label-width="120px"
        :rules="rules"
        class="form"
      >
        <BaseElFormItem v-if="showInput(['update'])" :label="$t('member.membersManage.userStatus')" prop="memberStatus">
          <BaseElRadioGroup v-model="modifyFormData.memberStatus">
            <div class="flex flex-col gap-2">
              <BaseElRadio label="normal">{{ $t('member.membersManage.statusNormal') }}</BaseElRadio>
              <BaseElRadio label="warning">
                <span class="inline-flex items-center gap-1">
                  {{ $t('member.membersManage.statusWarning') }}
                  <el-tooltip :content="$t('member.membersManage.warningTooltip')" placement="top">
                    <i class="el-icon-info text-gray-60" />
                  </el-tooltip>
                </span>
              </BaseElRadio>
              <BaseElRadio label="blocked">
                <span class="inline-flex items-center gap-1">
                  {{ $t('member.membersManage.statusBlocked') }}
                  <el-tooltip :content="$t('member.membersManage.blockedTooltip')" placement="top">
                    <i class="el-icon-info text-gray-60" />
                  </el-tooltip>
                </span>
              </BaseElRadio>
            </div>
          </BaseElRadioGroup>
        </BaseElFormItem>

        <BaseElFormItem
          v-if="showInput(['update'])"
          :label="$t('member.membersManage.memberLevel')"
          prop="level"
        >
          <BaseElInput v-model="modifyFormData.level" />
        </BaseElFormItem>

        <BaseElFormItem v-if="showInput(['update'])" :label="$t('member.common.name.text')" prop="name">
          <BaseElInput v-model="modifyFormData.name" />
        </BaseElFormItem>

        <BaseElFormItem
          v-if="showInput(['update'])"
          :label="$t('member.common.phone.text')"
          prop="phone"
        >
          <BaseElInput v-model="modifyFormData.phone" disabled />
        </BaseElFormItem>

        <BaseElFormItem v-if="showInput(['update'])" :label="$t('member.common.address.text')" prop="address">
          <BaseElInput
            v-model="modifyFormData.address"
            :placeholder="$t('member.membersManage.addressPlaceholder')"
          />
        </BaseElFormItem>

        <BaseElFormItem v-if="showInput(['update'])" :label="$t('member.common.tag.text')" prop="tags">
          <BaseElSelect
            v-model="modifyFormData.mTags"
            :placeholder="$t('member.membersManage.selectTag')"
            :no-data-text="$t('member.common.table.empty.text')"
            multiple
          >
            <BaseElSelectOption
              v-for="item in tags"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </BaseElSelect>
        </BaseElFormItem>

        <BaseElFormItem v-if="showInput(['use', 'give'])" :label="$t('member.membersManage.dialogUser')">
          <span>{{ modifyFormData.name }}</span>
        </BaseElFormItem>

        <BaseElFormItem
          v-if="showInput(['use', 'give'])"
          :label="$t('member.membersManage.depositAmount')"
          prop="coin"
        >
          <BaseElInput
            v-model="modifyFormData.coin"
            :placeholder="$t('member.membersManage.depositPlaceholder')"
          />
        </BaseElFormItem>

        <BaseElFormItem
          v-if="showInput(['use', 'give'])"
          :label="$t('member.common.note.text')"
          prop="note"
        >
          <BaseElInput
            v-model="modifyFormData.note"
            :placeholder="$t('member.common.note.text')"
            type="textarea"
          />
        </BaseElFormItem>
      </BaseElForm>

      <BaseTable v-if="showInput(['record'])" :empty-text="$t('member.membersManage.emptyText')">
        <BaseElTableColumn prop="UserInfo.email" :label="$t('member.membersManage.amount')" align="center" />
        <BaseElTableColumn
          prop="UserInfo.email"
          :label="$t('member.membersManage.usedDate')"
          align="center"
        />
        <BaseElTableColumn prop="UserInfo.email" :label="$t('member.common.note.text')" align="center">
          <template slot-scope="scope">
            {{ scope.row.UserInfo.email || '-' }}
          </template>
        </BaseElTableColumn>
      </BaseTable>

      <div slot="footer">
        <BaseElButton
          plain
          @click=";(showDialog = false), resetForm()"
        >
          {{ $t('member.common.back.text') }}
        </BaseElButton>
        <BaseElButton type="primary" @click="dialogConfirm">{{ $t('member.common.button.confirm.text') }}</BaseElButton>
      </div>
    </el-dialog>

    <ExportOptionsDialog
      v-if="showExportOptions"
      allRange
      :useExportTask="useExportTask"
      @close="showExportOptions = false"
      @export="prepareExport()"
    />
    <ExportDialog
      v-if="showExportDialog"
      :inProgress="exportting"
      :isError="exportError"
      :percentage="exportPercentage"
      :data="exportData"
      :total="membersCount"
      @close="resetExport"
    />
    <WarningDialog
      v-if="dialog.memberRegisterLimitWarning"
      hideCancel
      :content="$t('member.membersManage.registerLimitWarning')"
      @close="dialog.memberRegisterLimitWarning = false"
      @confirm="dialog.memberRegisterLimitWarning = false"
    >
      <template slot="body">
        <div style="padding-top: 16px">
          <a class="text-primary-100 underline" :href="bobLineAt" target="_blank" rel="noopener noreferrer">
            {{ $t('member.membersManage.contactSupport') }}
          </a>
        </div>
      </template>
    </WarningDialog>
  </div>
</template>

<script>
import { SimpleFindCompanyContactMenu } from '@/api/company/contactMenu'
import TableFilter from '@/components/Button/TableFilter.vue'
import WarningDialog from '@/components/Dialog/WarningDialog.vue'
import ExportOptionsDialog from '@/components/Dialog/ExportOptionsDialog.vue'
import ExportDialog from '@/components/Dialog/ExportDialog.vue'
import EmptyBlock from '@/components/EmptyBlock.vue'
import { mapGetters } from 'vuex'
import { memberManageFilterOptions } from '@/config/table'
import { bobLineAt } from '@/config/line'
import {
  GetMembersCount,
  GetMembers,
  UpdateMember,
} from '@/api/member'
import { CreateMemberSheetExportTask } from '@/api/exportTask'
import { GetHistoryExport } from '@/api/memberHistory'
import { useTableFilter } from '@/use/tableFilter'
// Utils
import { imgSrc } from '@/utils/helper'
import cities from '@/assets/data/cities'
import { pageStartIndex as getPageStartIndex } from '@/utils/table'
import formUtils from '@/utils/form'
import { phoneRules, noEmptyRules } from '@/validation'
import { ExportMoreSheetExcel } from '@/utils/excel'
import dayjs from '@/lib/dayjs'
import { checkUserFeature } from '@/store/modules/permission'
import { map, filter, get, find, mapValues, join, concat, includes, forEach, reduce } from 'lodash'
import { formatDate } from '@/utils/date'
import { handleMemberOrigins } from '@/utils/member'
import {
  GetUserInfoSetting,
} from '@/api/org/org'
import TagSearch from '@/components/Search/TagSearch'
import { defineComponent, ref, computed, reactive } from 'vue'
import { useTagStore } from '@/views/Marketing/components/useTagStore'
import { useCompany } from '@/use/company'
import { usePermissions } from '@/use/permissions'
import { useExportCenter } from '@/use/useExportCenter'
import MemberLimitProgressBar from '@/components/MemberLimitProgressBar.vue'
import CustomUserInfoFieldsSelect from '@/components/Select/CustomUserInfoFieldsSelect.vue'
import { useShop } from '@/use/shop'
import { useRoute } from 'vue-router/composables'
import { useOrg } from '@/use/useOrg'
import SearchTextInput from '@/components/Input/SearchTextInput.vue'
import { t } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'MembersManage',
  components: {
    // MemberSearch,
    SearchTextInput,
    EmptyBlock,
    ExportDialog,
    ExportOptionsDialog,
    TagSearch,
    WarningDialog,
    TableFilter,
    MemberLimitProgressBar,
    CustomUserInfoFieldsSelect,
  },
  setup () {
    const tableName = 'memberManage'
    const { shopId, shopMemberLevelConfig } = useShop()
    const { orgId } = useOrg()
    const { tags, fetchTagsOnMounted } = useTagStore()
    const { useCompanyContactMenu, getShopContactMenuInfo, orgTaxId } = useCompany()
    const { messageOptions } = useExportCenter()
    const { checkAction } = usePermissions()
    const {
      tableRefresh,
      updateTableFilter,
      showColumn,
      getTableFilterConfig,
      tableFilter,
    } = useTableFilter(tableName, memberManageFilterOptions)
    const route = useRoute()
    const search = reactive({
      searchText: '',
      name: '',
      phone: '',
      birthdayMonth: '',
      tags: '',
      member: undefined,
    })
    const customFieldFilterModel = reactive({
      key: undefined,
      value: undefined,
    })
    const memberAuthLineDisplayName = ref(undefined)
    const loading = ref(false)
    const exportError = ref(false)
    const memberLimitProgressBarRef = ref(null)
    const userInfoSetting = ref([])
    const membersList = ref([])
    const membersCount = ref(0)
    const levelList = computed(() => filter(shopMemberLevelConfig.value, c => c.level !== 0))
    const nullLevelName = computed(() => get(find(shopMemberLevelConfig.value, { level: 0 }), 'name', ''))
    const tableOptions = reactive({
      page: 1,
      pageLimit: 10,
    })

    const pageStartIndex = computed(() => {
      return getPageStartIndex(tableOptions.page, tableOptions.pageLimit)
    })

    const queryPage = computed(() => {
      return get(route, 'query.page') || 1
    })

    const useExportTask = computed(() => checkAction('admin.member.createSheetExportTask'))
    // 店家方案沒有會員等級（GIMMATEK 不提供）時，不顯示等級欄位與匯出欄（CNCY-10766）
    const showMemberLevel = computed(() => checkAction('admin.memberLevelConfig.page'))
    const showMemberLimit = computed(() => checkAction('admin.member.allowAll'))
    fetchTagsOnMounted()

    const displayTableFilterOptions = computed(() => {
      const userInfoFields = map(filter(userInfoSetting.value, (item) => {
        return !includes(['phone', 'name', 'birthday', 'address'], item.key) && !includes(['restrictedImage'], item.type)
      }), (item) => {
        return {
          label: item.name,
          value: item.key,
        }
      })

      // 加入 LINE 好友名稱 選項
      const lineDisplayNameOption = { label: t('member.membersManage.lineFriendName'), value: 'lineDisplayName' }

      const baseOptions = showMemberLevel.value
        ? memberManageFilterOptions
        : filter(memberManageFilterOptions, (option) => option.value !== 'level')

      return concat(baseOptions, [lineDisplayNameOption], userInfoFields)
    })

    const displayCustomFieldColumns = computed(() => {
      const fields = filter(userInfoSetting.value, (item) => { return !includes(['phone', 'name', 'birthday', 'address'], item.key) && !includes(['restrictedImage'], item.type) })
      return fields
    })

    const displayTableData = computed(() => {
      return map(membersList.value, (item) => {
        const row = {
          ...item,
          customFields: {},
        }

        for (const field of displayCustomFieldColumns.value) {
          const fieldType = get(field, 'type')
          const fieldKey = get(field, 'key')
          switch (fieldType) {
          case 'email':
            row.customFields[fieldKey] = get(row, `UserInfo.${fieldKey}`)
            break
          case 'gender':
            if (get(row, `UserInfo.${fieldKey}`) === 'male') {
              row.customFields[fieldKey] = t('member.common.male.text')
            } else if (get(row, `UserInfo.${fieldKey}`) === 'female') {
              row.customFields[fieldKey] = t('member.common.female.text')
            } else {
              row.customFields[fieldKey] = get(row, `UserInfo.${fieldKey}`)
            }
            break
          case 'checkbox':
            row.customFields[fieldKey] = join(get(row, `UserInfo.custom.${fieldKey}`), ', ') || '-'
            break
          case 'radio':
            row.customFields[fieldKey] = get(row, `UserInfo.custom.${fieldKey}`) || '-'
            break
          case 'radioSubField': {
            const radioSubData = get(row, `UserInfo.custom.${fieldKey}`)
            if (radioSubData && radioSubData.parentValue && radioSubData.childValue) {
              row.customFields[fieldKey] = `${radioSubData.parentValue} - ${radioSubData.childValue}`
            } else {
              row.customFields[fieldKey] = '-'
            }
            break
          }
          case 'checkboxSubField': {
            const checkboxSubData = get(row, `UserInfo.custom.${fieldKey}`) || []
            if (Array.isArray(checkboxSubData) && checkboxSubData.length > 0) {
              row.customFields[fieldKey] = join(filter(map(checkboxSubData, item => {
                if (item.parentValue && item.childValue) {
                  return `${item.parentValue} - ${item.childValue}`
                }
                return ''
              }), Boolean), ', ') || '-'
            } else {
              row.customFields[fieldKey] = '-'
            }
            break
          }
          default:
            row.customFields[fieldKey] = get(row, `UserInfo.custom.${fieldKey}`, '-')
          }
        }

        return row
      })
    })

    // ANCHOR 取得會員資料
    const getMembers = async () => {
      try {
        // build payload and avoid sending customValue for subField types
        const targetField = find(userInfoSetting.value, f => f.key === customFieldFilterModel.key)
        const isSubFieldType = targetField && includes(['radioSubField', 'checkboxSubField'], targetField.type)

        const payload = {
          shopId: shopId.value,
          start: pageStartIndex.value,
          limit: tableOptions.pageLimit,
          name: search.name === '' ? undefined : search.name,
          phone: search.phone === '' ? undefined : search.phone,
          search: search.searchText || undefined,
          mTagId: search.tags || undefined,
          customKey: customFieldFilterModel.key || undefined,
          // only include customValue for non-subField types
          ...(isSubFieldType ? {} : { customValue: customFieldFilterModel.value || undefined }),
          // always include parent/child params if present
          customParentValue: (customFieldFilterModel.value && customFieldFilterModel.value.parentValue) || undefined,
          customChildValue: (customFieldFilterModel.value && customFieldFilterModel.value.childValue) || undefined,
          memberAuthLineDisplayName: memberAuthLineDisplayName.value || undefined,
        }

        // 針對某些特殊自訂義欄位做資料單獨的例外處理
        if (payload.customKey === 'email') {
          payload.email = payload.customValue
          delete payload.customKey
          delete payload.customValue
        } else if (payload.customKey === 'birthday') {
          payload.birthdayMonth = payload.customValue
          delete payload.customKey
          delete payload.customValue
        } else if (payload.customKey === 'gender') {
          payload.gender = payload.customValue
          delete payload.customKey
          delete payload.customValue
        } else if (payload.customKey === 'address') {
          payload.address = payload.customValue
          delete payload.customKey
          delete payload.customValue
        }

        const res = await GetMembers(payload)
        membersList.value = res
      } catch (error) {
        exportError.value = true
        window.$message.error({
          message: error || error.message,
        })
      }
    }

    const getMemberCount = async () => {
      try {
        const targetFieldCount = find(userInfoSetting.value, f => f.key === customFieldFilterModel.key)
        const isSubFieldTypeCount = targetFieldCount && includes(['radioSubField', 'checkboxSubField'], targetFieldCount.type)

        const payload = {
          shopId: shopId.value,
          name: search.name === '' ? undefined : search.name,
          phone: search.phone === '' ? undefined : search.phone,
          search: search.searchText || undefined,
          mTagId: search.tags || undefined,
          customKey: customFieldFilterModel.key || undefined,
          ...(isSubFieldTypeCount ? {} : { customValue: customFieldFilterModel.value || undefined }),
          customParentValue: (customFieldFilterModel.value && customFieldFilterModel.value.parentValue) || undefined,
          customChildValue: (customFieldFilterModel.value && customFieldFilterModel.value.childValue) || undefined,
          memberAuthLineDisplayName: memberAuthLineDisplayName.value || undefined,
        }

        // 針對某些特殊自訂義欄位做資料單獨的例外處理
        if (payload.customKey === 'email') {
          payload.email = payload.customValue
          delete payload.customKey
          delete payload.customValue
        } else if (payload.customKey === 'birthday') {
          payload.birthdayMonth = payload.customValue
          delete payload.customKey
          delete payload.customValue
        } else if (payload.customKey === 'gender') {
          payload.gender = payload.customValue
          delete payload.customKey
          delete payload.customValue
        } else if (payload.customKey === 'address') {
          payload.address = payload.customValue
          delete payload.customKey
          delete payload.customValue
        }

        membersCount.value = await GetMembersCount(payload)
      } catch (error) {
        exportError.value = true
        window.$message.error({
          message: error || error.message,
        })
      }
    }

    const getUserInfoSetting = async () => {
      const [res, err] = await GetUserInfoSetting({
        orgId: orgId.value,
      })

      if (err) {
        if (err.status === 404) return
        return window.$message.error(err)
      }

      userInfoSetting.value = res.fields
    }

    const refresh = async (search = false) => {
      loading.value = true
      if (search) { tableOptions.page = 1 }
      if (!search) { tableOptions.page = Number(queryPage.value) }
      getMembers()
      getMemberCount()

      await getUserInfoSetting()
      loading.value = false
    }

    const onCustomFieldFilterChange = (payload) => {
      const fieldKey = get(payload, 'field.key')
      const contextValue = get(payload, 'contextValue')

      // 如果是 LINE 好友名稱,使用特殊參數
      if (fieldKey === 'lineDisplayName') {
        memberAuthLineDisplayName.value = contextValue
        customFieldFilterModel.key = undefined
        customFieldFilterModel.value = undefined
      } else {
        customFieldFilterModel.key = fieldKey
        customFieldFilterModel.value = contextValue
        memberAuthLineDisplayName.value = undefined
      }

      refresh(true)
    }

    const onCustomFieldFilterClear = async () => {
      customFieldFilterModel.key = undefined
      customFieldFilterModel.value = undefined
      memberAuthLineDisplayName.value = undefined
      refresh(true)
    }

    return {
      displayTableData,
      displayTableFilterOptions,
      displayCustomFieldColumns,
      queryPage,
      tags,
      useCompanyContactMenu,
      getShopContactMenuInfo,
      orgTaxId,
      tableRefresh,
      tableName,
      tableFilter,
      showColumn,
      memberManageFilterOptions,
      updateTableFilter,
      getTableFilterConfig,
      useExportTask,
      showMemberLevel,
      messageOptions,
      memberLimitProgressBarRef,
      showMemberLimit,
      loading,
      exportError,
      customFieldFilterModel,
      memberAuthLineDisplayName,
      search,
      userInfoSetting,
      membersCount,
      levelList,
      nullLevelName,
      tableOptions,
      bobLineAt,
      pageStartIndex,
      refresh,
      onCustomFieldFilterChange,
      onCustomFieldFilterClear,
    }
  },
  data: () => ({
    mounted: false,
    // Export
    showExportOptions: false,
    exportting: false,

    exportData: null,
    exportMemberHistoryData: [],
    exportPercentage: 0,
    showExportDialog: false,

    citiesList: cities,

    dialog: {
      memberRegisterLimitWarning: false,
    },

    walletRecordTableOptions: {
      page: 1,
      pageLimit: 10,
    },

    // Dialog
    showDialog: false,
    deleteDialog: false,
    dialogType: '',
    selectRow: null,

    company: [],

    modifyFormData: {
      memberStatus: 'normal',
      level: '',
      name: '',
      phone: '',
      city: '',
      area: '',
      address: '',
      mTags: [],
      coin: '',
      note: '',
    },

    // Form Rules
    rules: {
      name: [
        { required: true, message: t('member.membersManage.validation.nameRequired'), trigger: 'blur' },
        {
          min: 2,
          max: 10,
          message: t('member.membersManage.validation.nameLength'),
          trigger: 'blur',
        },
      ],
      phone: phoneRules(),
      address: noEmptyRules(),
      coin: noEmptyRules(),
    },

  }),

  computed: {
    ...mapGetters([
      'shop',
      'userPlanFeature',
      'userFeatures',
      'orgId',
    ]),

    // useCompanyContactMenu () {
    //   return checkUserFeature(this.userPlanFeature, this.userFeatures, 'admin.shopCompanyDirectory.page') && find(this.customUserSetting, { type: 'companyDirectoryFirmTaxId' })
    // },
    walletRecordPageStartIndex () {
      return getPageStartIndex(
        this.walletRecordTableOptions.page,
        this.walletRecordTableOptions.pageLimit,
      )
    },
    cityList () {
      const list = []
      forEach(this.citiesList, (item) => {
        if (!includes(list, item.city)) {
          list.push(item.city)
        }
      })
      return list
    },
  },

  watch: {
    async queryPage () {
      this.tableOptions.page = Number(this.queryPage)
      await this.refresh()
    },
  },
  beforeMount () {
    this.tableOptions.page = Number(this.queryPage)
  },

  async mounted () {
    this.mounted = true
    await this.loadData()
  },
  async activated () {
    await this.loadData()
  },
  // ANCHOR Methods
  methods: {
    formatDate,
    async loadData () {
      this.tableFilter = this.getTableFilterConfig()
      this.tableOptions.page = Number(this.queryPage)
      await this.refresh()

      await this.getShopContactMenuInfo({ shopId: this.shop })
      if (!this.useCompanyContactMenu || !this.orgTaxId(this.customUserSetting)) return
      this.getCompany()
    },
    async getCompany () {
      let start = 0
      const limit = 10
      const data = []
      let temp = []
      const [res, err] = await SimpleFindCompanyContactMenu({
        shopId: this.shop,
        start,
        limit,
      })
      if (err) {
        this.$message.error(err)
        return
      }
      start += res.length
      temp = res
      data.push(...res)

      while (temp.length === limit) {
        const [res, err] = await SimpleFindCompanyContactMenu({
          shopId: this.shop,
          start,
          limit,
        })
        if (err) {
          this.$message.error(err)
          return
        }
        start += res.length
        temp = res
        data.push(...res)
      }
      this.company = data
    },
    // ANCHOR 處理使用者匯出資料
    async prepareExport () {
      if (this.useExportTask) {
        await this.createMemberSheetExportTask()
      } else {
        const permission = checkUserFeature(this.userPlanFeature, this.userFeatures, 'admin.member.exportExcel')
        if (!permission) return this.$message.error(t('member.membersManage.noExportPermission'))
        if (!this.membersCount) {
          this.$message.warning(t('member.membersManage.noDataToExport'))
          return
        }
        this.exportError = false
        this.showExportDialog = true
        this.exportting = true
        await this.batchGetAllMember()
        await this.getAllMemberHistory()
        await this.formatExportData()
        this.exportting = false
      }
      this.showExportOptions = false
    },
    async createMemberSheetExportTask () {
      const payload = {
        shopId: this.shop,
        name: this.search.name === '' ? undefined : this.search.name,
        phone: this.search.phone === '' ? undefined : this.search.phone,
        search: this.search.searchText || undefined,
        birthdayMonth:
          this.search.birthdayMonth === ''
            ? undefined
            : this.search.birthdayMonth,
        mTagId: this.search.tags || undefined,
        customKey: this.customFieldFilterModel.key || undefined,
        customValue: this.customFieldFilterModel.value || undefined,
        memberAuthLineDisplayName: this.memberAuthLineDisplayName || undefined,
      }
      // 針對某些特殊自訂義欄位做資料單獨的例外處理
      if (payload.customKey === 'email') {
        payload.email = payload.customValue
        delete payload.customKey
        delete payload.customValue
      } else if (payload.customKey === 'birthday') {
        payload.birthdayMonth = payload.customValue
        delete payload.customKey
        delete payload.customValue
      } else if (payload.customKey === 'gender') {
        payload.gender = payload.customValue
        delete payload.customKey
        delete payload.customValue
      } else if (payload.customKey === 'address') {
        payload.address = payload.customValue
        delete payload.customKey
        delete payload.customValue
      }
      const [, err] = await CreateMemberSheetExportTask(payload)
      if (err) {
        this.$message.error(err)
        return
      }
      this.$message(this.messageOptions)
    },
    resetExport () {
      this.showExportDialog = false
      this.exportting = false
      this.exportError = false
      this.exportData = null
      this.exportPercentage = 0
    },
    // ANCHOR 處理匯出資料格式
    async formatExportData () {
      const data = []
      let count = 0
      const total = this.membersCount + this.exportMemberHistoryData.length

      const showPaymentSum = checkUserFeature(this.userPlanFeature, this.userFeatures, 'adminView.member.exportWithShowSalesRecordTotalPaymentPriceSum')

      forEach(this.exportData, (item) => {
        // 登入方式
        const logins = map(item.User, (login, key) => {
          if (login) {
            if (key === 'AuthFB') return 'Facebook'
            if (key === 'AuthLine') return 'Line'
            if (key === 'AuthPhoneAccount') return t('member.membersManage.loginPhoneAccount')
          }
          return ''
        })
        const tags = map(item.MTags, 'name')
        let row = {
          [t('member.common.name.text')]: item.UserInfo.name,
          [t('member.common.phone.text')]: item.UserInfo.phone,
          [t('member.common.birthday.text')]: item.UserInfo.birthday ? dayjs(item.UserInfo.birthday).format('YYYY-MM-DD') : '',
          [t('member.common.gender.text')]: item.UserInfo.gender ? (item.UserInfo.gender === 'male' ? t('member.common.male.text') : t('member.common.female.text')) : '',
          ...(this.showMemberLevel ? { [t('member.membersManage.memberLevel')]: item.level } : {}),
          [t('member.common.tag.text')]: tags.join(','),
          [t('member.common.address.text')]: item.UserInfo.address,
          Email: item.UserInfo.email || '',
          [t('member.membersManage.userStatus')]: this.memberStatusLabel(item.memberStatus, item.isBlock),
          [t('member.membersManage.loginType')]: filter(logins, i => i).join(', '),
          [t('member.membersManage.lineBinding')]: item.User && item.MemberAuthLine ? t('member.common.yes.text') : t('member.common.no.text'),
          [t('member.membersManage.source')]: handleMemberOrigins({ origin: item.origin, originText: item.originText }),
          [t('member.common.createdAt.text')]: formatDate(item.createdAt, 'YYYY-MM-DD'),
          [t('member.common.createdAtTime.text')]: formatDate(item.createdAt, 'YYYY-MM-DD HH:mm'),
        }

        // -- 匯出自定義欄位
        const userCustom = item.UserInfo.custom
        if (userCustom) {
          const customContent = (custom, content) => {
            if (custom === 'taxId') {
              const company = find(this.company, { taxId: content })
              if (company) return `${company.name} (${content})`
            }
            return content
          }
          for (const custom in userCustom) {
            let content = ''

            if (Array.isArray(userCustom[custom])) {
              content = reduce(userCustom[custom], (previous, current, index) => {
                return previous + (index ? '\n' : '') + current
              }, '')
            } else content = userCustom[custom]

            if (this.convertUserInfoSettingName(custom)) {
              row = {
                ...row,
                [this.convertUserInfoSettingName(custom)]: customContent(custom, content),
              }
            }
          }
        }
        if (showPaymentSum) {
          row[t('member.membersManage.totalSpent')] = (item.salesRecordTotalPaymentPriceSum - item.salesRecordTotalWalletAmountSum).toString()
        }
        data.push(row)
        count++
        this.exportPercentage = (count / total) * 100
      })

      const historyData = []
      forEach(this.exportMemberHistoryData, item => {
        const _fileLink = item.FileId
          ? location.origin + this.$router.resolve({
            name: 'Read Member History File',
            params: { shopId: this.shop, id: item.id, downloadKey: item.fileFreeDownloadKey },
          }).href
          : '-'

        const _images = join(map(item.Images, i => imgSrc(800, i)), '\n')
        const _name = get(item, 'Member.UserInfo.name', '-')
        const _phone = get(item, 'Member.UserInfo.phone', '-')

        const row = {
          [t('member.common.name.text')]: _name,
          [t('member.common.phone.text')]: _phone,
          [t('member.membersManage.historyPublicShow')]: item.showMember ? t('member.common.yes.text') : t('member.common.no.text'),
          [t('member.membersManage.historyMemberNote')]: item.memberNote,
          [t('member.membersManage.historyShopNote')]: item.note,
          [t('member.membersManage.historyImageLink')]: _images,
          [t('member.membersManage.historyFileLink')]: _fileLink,
          [t('member.common.createdAt.text')]: formatDate(item.createdAt, 'YYYY-MM-DD'),
          [t('member.common.createdAtTime.text')]: formatDate(item.createdAt, 'YYYY-MM-DD HH:mm'),
          [t('member.common.updatedAt.text')]: formatDate(item.updatedAt, 'YYYY-MM-DD'),
          [t('member.common.updatedAtTime.text')]: formatDate(item.updatedAt, 'YYYY-MM-DD HH:mm'),
        }
        historyData.push(row)
        count++

        this.exportPercentage = (count / total) * 100
      })
      ExportMoreSheetExcel([
        {
          sheetName: t('member.membersManage.sheetNameMemberData'),
          data,
        },

        {
          sheetName: t('member.membersManage.sheetNameServiceHistory'),
          data: historyData,
        },
      ], t('member.membersManage.sheetNameMemberData'))
    },

    tagType (val) {
      let type = 'info'
      // if (!val) type = 'success'
      if (val) type = 'danger'
      return type
    },
    /**
     * 根據 memberStatus 返回 Tag 類型
     * @param {string} status - 'normal' | 'warning' | 'blocked'
     * @param {boolean} [isBlockFallback] - 舊欄位向後相容
     */
    memberStatusTagType (status, isBlockFallback) {
      const resolved = status || (isBlockFallback ? 'blocked' : 'normal')
      if (resolved === 'blocked') return 'danger'
      if (resolved === 'warning') return 'warning'
      return 'info'
    },
    /**
     * 根據 memberStatus 返回顯示文字
     * @param {string} status - 'normal' | 'warning' | 'blocked'
     * @param {boolean} [isBlockFallback] - 舊欄位向後相容
     */
    memberStatusLabel (status, isBlockFallback) {
      const resolved = status || (isBlockFallback ? 'blocked' : 'normal')
      if (resolved === 'blocked') return t('member.membersManage.statusBlockedShort')
      if (resolved === 'warning') return t('member.membersManage.statusWarningShort')
      return t('member.membersManage.statusNormalShort')
    },

    goProfile (row) {
      this.$router.push(`/members/member-profile/${row.id}`)
    },
    showInput (typeList) {
      return includes(typeList, this.dialogType)
    },

    async changePage (page) {
      this.tableOptions.page = page
      this.$router.push({
        name: 'MembersManage',
        query: {
          page: page || 1,
        },
      })
      // await this.refresh(false)
    },

    syncFormData (row) {
      this.modifyFormData.name = row.UserInfo.name
      this.modifyFormData.phone = row.UserInfo.phone
      this.modifyFormData.level = row.level
      this.modifyFormData.memberStatus = row.memberStatus || (row.isBlock ? 'blocked' : 'normal')
      this.modifyFormData.address = row.UserInfo.address
      this.modifyFormData.mTags = map(row.MTags, 'id')
    },

    //= > 重置表單
    resetForm () {
      if (this.dialogType === 'record') return
      formUtils.resetForm(this.$refs.form)
      this.modifyFormData = {
        memberStatus: 'normal',
        level: '',
        name: '',
        phone: '',
        city: '',
        area: '',
        address: '',
        mTags: [],
        coin: '',
        note: '',
      }
    },

    openDialog (type) {
      this.dialogType = type
      this.showDialog = true
    },

    async dialogConfirm () {
      const type = this.dialogType
      if (!(await formUtils.checkForm(this.$refs.form))) return
      if (type === 'update') this.updateMember()
      if (type === 'use') this.useDepositWallet()
      if (type === 'give') this.giveDepositWallet()
      this.showDialog = false
    },

    getDisplayLevel (row) {
      const curLevel = row.level
      if (!curLevel) return this.nullLevelName || t('member.membersManage.noLevel')
      return get(find(this.levelList, { level: curLevel }), 'name')
    },

    //= > 更新會員
    async updateMember () {
      try {
        await UpdateMember({
          shopId: this.shop,
          id: this.selectRow.id,
          memberStatus: this.modifyFormData.memberStatus,
          level: this.modifyFormData.level,
          name: this.modifyFormData.name,
          phone: undefined,
          address: this.modifyFormData.address,
          mTags: this.modifyFormData.mTags,
        })
        this.refresh()
        this.$message.success(t('member.membersManage.updateSuccess'))
      } catch (error) {
        console.log(error)
        this.$message.error({
          message: error || error.message,
        })
      }
    },

    // ANCHOR 批次取得所有會員資料
    async batchGetAllMember () {
      let start = 0
      const data = []
      const _limit = 100

      const { name, phone, birthdayMonth, tags } = this.search
      const search = mapValues({ name, phone, birthdayMonth }, v => v === '' ? undefined : v)

      const showPaymentSum = checkUserFeature(this.userPlanFeature, this.userFeatures, 'adminView.member.exportWithShowSalesRecordTotalPaymentPriceSum')

      while (true) {
        const [res, err] = await GetMembers({
          shopId: this.shop,
          start,
          limit: _limit,
          ...search,
          mTagId: tags === '' ? undefined : tags.id,
          showSalesRecordTotalPaymentPriceSum: showPaymentSum,
          customKey: this.customFieldFilterModel.key || undefined,
          customValue: this.customFieldFilterModel.value || undefined,
          customParentValue: (this.customFieldFilterModel.value && this.customFieldFilterModel.value.parentValue) || undefined,
          customChildValue: (this.customFieldFilterModel.value && this.customFieldFilterModel.value.childValue) || undefined,
        }).then(res => [res, null])
          .catch(err => [null, err])
        if (err) {
          return [null, err]
        }
        start += _limit
        data.push(...res)
        if (res.length < _limit) {
          break
        }
      }
      this.exportData = data
      return [data, null]
    },
    // ANCHOR 取得會員服務紀錄
    async getAllMemberHistory () {
      let start = 0
      const data = []
      const _limit = 100

      // TODO PYZU-2240
      // const { name, phone, birthdayMonth, tags } = this.search
      // const search = mapValues({ name, phone, birthdayMonth }, v => v === '' ? undefined : v)
      // eslint-disable-next-line no-unreachable-loop
      while (true) {
        const [res, err] = await GetHistoryExport({
          shopId: this.shop,
          start,
          limit: _limit,
          // ...search,
          // mTagId: tags === '' ? undefined : tags.id,
        }).then(res => [res, null])
          .catch(err => [null, err])
        if (err) {
          return [null, err]
        }
        start += _limit
        data.push(...res)
        if (res.length < _limit) {
          break
        }
      }
      this.exportMemberHistoryData = data
      return [data, null]
    },

    convertUserInfoSettingName (key) {
      const target = find(this.userInfoSetting, { key })

      return target?.name || ''
    },
    handleCreateMember () {
      if (this.memberLimitProgressBarRef?.reachedLimit) {
        this.dialog.memberRegisterLimitWarning = true
        return
      }
      this.$router.push({ name: 'MemberCreate' })
    },
  },
})
</script>

<style scoped lang="postcss">

.login-auth {
  @apply flex justify-center gap-[4px];
}

.members-manage__search {
  flex: 1 1 auto;
}
</style>
